import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAddOrEditChild } from '@/features/profile/MinorChildren/ChildModal/useAddOrEditChild';
import { Utils } from '@/shared/Utils';
import { ChildModalMode, ChildModalProps, IFormKid } from './ChildModal.types';
import { IKid } from '@/shared/interfaces';

export const useChildForm = ({
    mode,
    kid,
    onMutationSuccess,
    onClose,
}: ChildModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm<IFormKid>({
        defaultValues: createDefaultFormValues(mode, kid),
        mode: 'onBlur',
    });

    const [showModulusWarning, setShowModulusWarning] = useState(false);

    const [runningOnMutationSuccess, setRunningOnMutationSuccess] =
        useState(false);

    const { mutate, status, reset, errorMessage } = useAddOrEditChild({ mode });

    const isProcessing = status === 'pending' || runningOnMutationSuccess;

    const onSuccess = async () => {
        setRunningOnMutationSuccess(true);
        try {
            await onMutationSuccess?.();
        } finally {
            setRunningOnMutationSuccess(false);
            hideModulusWarning();
            onClose();
        }
        reset();
    };

    const onError = () => {
        hideModulusWarning();
    };

    const onSubmit = () => {
        if (status === 'pending') return;

        const data = getValues();

        if (!Utils.cprModulusCheck(data.CPR) && !showModulusWarning) {
            setShowModulusWarning(true);
            return;
        }

        mutate(data, { onSuccess, onError });
    };

    const hideModulusWarning = () => setShowModulusWarning(false);

    return {
        register,
        handleSubmit,
        errors,
        onSubmit,
        status,
        isProcessing,
        reset,
        errorMessage,
        showModulusWarning,
        hideModulusWarning,
        getValues,
    };
};

const createDefaultFormValues = (
    mode: ChildModalMode,
    kid?: IKid,
): IFormKid => {
    if (mode.isEditing && kid)
        return (({ FormattedCpr, ...rest }) => rest)(kid);

    if (mode.isAdding)
        return {
            Name: '',
            CPR: '',
        };
    return undefined;
};
